import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Input, message, Modal, Popconfirm, Radio, Spin, Table, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { UserName } from '../name_component/user/UserNameComponent';
import UserRoleTags from './UserRoleTags';
import useMountEffect from '../../hooks/useMountEffect';
import { fetchFacilityUsers } from '../../thunks/users_thunks';
import { userGroupsRestCrudThunks } from '../../thunks/user_groups_thunks';
import { withUserType } from '../withUserType';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { statusIndicatorFormatter } from '../../utils/DataFormatterUtils';

require('./UserGroupDetailModal.less');

// Status buckets. The precedence (invited wins over active) mirrors
// `statusIndicatorFormatter`, which is what the Account Status column renders, so the filter
// counts can never disagree with the dot shown on the row.
const STATUS_ALL = 'all';
const STATUS_ACTIVE = 'active';
const STATUS_INACTIVE = 'inactive';
const STATUS_INVITED = 'invited';

// Returns null when the member could not be resolved to a facility user, so its status is
// genuinely unknown rather than wrongly reported as inactive.
const accountStatusOf = (user) => {
	const account = nullSafeGet('account', user);
	if (!account) {
		return null;
	}
	if (nullSafeGetOrElse('isInvited', account, false)) {
		return STATUS_INVITED;
	}
	return nullSafeGetOrElse('isActive', account, false) ? STATUS_ACTIVE : STATUS_INACTIVE;
};

// `UserName` declares its click handler as a destructuring default:
//   onClick = (e) => { e.preventDefault(); e.stopPropagation(); }
// Destructuring defaults apply when the passed value is `undefined`, so `onClick={undefined}`
// ACTIVATES that default rather than clearing it — which swallowed the click that selects an
// option. A real no-op function is required to override it.
const noopClick = () => {};

const emailOf = (user) => nullSafeGetOrElse('contact.email', user, '').toLowerCase();

// Group detail: the group's members, filterable by account status and searchable, with
// multi-select removal.
//
// Members are resolved by intersecting the group's `memberEmails` with the facility user list
// (/admin/facility_users_list) — the same source the member picker uses, and the one the
// backend's own controller comment nominates for this listing. That endpoint is unpaginated,
// so it is fetched once on mount and filtered in memory.
const UserGroupDetailModal: FC<any> = ({
	userGroup,
	onCancel,
	onEditClick,
	onChanged,
	fetchUsers,
	removeMembers,
	userType,
	editLoading,
}): React.ReactElement => {
	const [facilityUsers, setFacilityUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState('');
	const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
	const [selectedEmails, setSelectedEmails] = useState([]);
	const [removing, setRemoving] = useState(false);

	useMountEffect(() => {
		fetchUsers()
			.then((fetched) => setFacilityUsers(fetched || []))
			.catch(() => setFacilityUsers([]))
			.finally(() => setLoading(false));
	});

	const memberEmails = useMemo(
		() =>
			nullSafeGetOrElse('memberEmails', userGroup, []).map((email) => (email || '').toLowerCase()),
		[userGroup]
	);

	// One row per member email, joined to the facility user list where possible.
	//
	// Buyer groups are company-wide, but the only user listing available to the client is
	// facility-scoped (/admin/facility_users_list), so a member belonging to another facility
	// of the same company cannot be resolved here. Those members still get a row — email only,
	// other cells blank — because dropping them would make members appear to vanish. Resolving
	// them properly needs a company-wide user endpoint, which the backend does not expose yet.
	const members = useMemo(() => {
		const usersByEmail = new Map();
		facilityUsers.forEach((user) => usersByEmail.set(emailOf(user), user));
		return memberEmails.map(
			(email) => usersByEmail.get(email) || { contact: { email }, account: null, role: [] }
		);
	}, [facilityUsers, memberEmails]);

	// Counts come from the server, not from the rows above. `activeUsersCount` and
	// `invitedUsersCount` are hydrated per group by the service; total is the membership size,
	// and inactive is the remainder (the service only buckets Active and Invited, so anything
	// else is inactive). Deriving these from the facility-joined rows would both undercount a
	// company-wide group and disagree with the numbers on the index row.
	const statusCounts = useMemo(() => {
		const total = memberEmails.length;
		const active = nullSafeGetOrElse('activeUsersCount', userGroup, 0);
		const invited = nullSafeGetOrElse('invitedUsersCount', userGroup, 0);
		return {
			[STATUS_ALL]: total,
			[STATUS_ACTIVE]: active,
			[STATUS_INVITED]: invited,
			[STATUS_INACTIVE]: Math.max(total - active - invited, 0),
		};
	}, [userGroup, memberEmails]);

	const visibleMembers = useMemo(() => {
		const term = searchText.trim().toLowerCase();
		return members.filter((user) => {
			// An unresolved member has no known status, so it appears under All only.
			if (statusFilter !== STATUS_ALL && accountStatusOf(user) !== statusFilter) {
				return false;
			}
			if (!term) {
				return true;
			}
			const name = `${nullSafeGetOrElse('contact.nameGiven', user, '')} ${nullSafeGetOrElse(
				'contact.nameFamily',
				user,
				''
			)}`.toLowerCase();
			return name.indexOf(term) !== -1 || emailOf(user).indexOf(term) !== -1;
		});
	}, [members, searchText, statusFilter]);

	const onSearchChange = useCallback((event) => setSearchText(event.target.value), []);

	const onStatusFilterChange = useCallback((event) => setStatusFilter(event.target.value), []);

	const onSelectionChange = useCallback((keys) => setSelectedEmails(keys), []);

	// Removal is an update of the whole membership list — there is no per-member endpoint, so
	// the group is PUT back with the selected emails filtered out. `name` is required by the
	// controller on every write, and re-sending the group's own name is explicitly allowed.
	const onRemoveSelected = useCallback(() => {
		const selectedSet = new Set(selectedEmails);
		const remaining = memberEmails.filter((email) => !selectedSet.has(email));
		setRemoving(true);
		const messageKey = 'user-group-remove-members';
		message.loading({ content: 'Removing users…', key: messageKey });
		removeMembers({
			id: nullSafeGet('id', userGroup),
			name: nullSafeGetOrElse('name', userGroup, ''),
			memberEmails: remaining,
		})
			.then((saved) => {
				message.success({
					content: `Removed ${selectedEmails.length} ${
						selectedEmails.length === 1 ? 'user' : 'users'
					}`,
					key: messageKey,
					duration: 1.5,
				});
				setSelectedEmails([]);
				// The write response is the freshly hydrated group (members + counts), so hand it
				// up rather than refetching; the index owns this record and feeds it back down.
				if (onChanged) {
					onChanged(saved);
				}
			})
			.catch((error) =>
				message.error({
					content: typeof error === 'string' && error ? error : 'Failed to remove users.',
					key: messageKey,
				})
			)
			.finally(() => setRemoving(false));
	}, [userGroup, memberEmails, onChanged, removeMembers, selectedEmails]);

	const columns = useMemo(
		() => [
			// Widths are authoritative here, not hints: `scroll.y` sets rc-table's `fixHeader`,
			// which forces `table-layout: fixed`. Name and Email get the room — they carry the
			// longest strings — while Account Status, Title and Department are trimmed back.
			{
				title: 'Name',
				dataIndex: ['contact', 'nameGiven'],
				width: 260,
				// An unresolved member carries only an email, so there is no name or avatar to
				// draw — the Email column still identifies them.
				render: (_, record) =>
					nullSafeGet('account', record) ? (
						<UserName
							contact={nullSafeGetOrElse('contact', record, {})}
							account={nullSafeGetOrElse('account', record, {})}
							userType={userType}
							mode="inline"
							showNameWithAvatar
							allowHover={false}
							underline={false}
							onClick={noopClick}
						/>
					) : (
						<span className="bodyregular text-character-secondary">--</span>
					),
			},
			{
				title: 'Account Status',
				dataIndex: ['account'],
				width: 130,
				render: (account) =>
					account
						? statusIndicatorFormatter(
								nullSafeGetOrElse('isInvited', account, false),
								nullSafeGetOrElse('isActive', account, false)
						  )
						: '--',
			},
			{
				title: 'Email',
				dataIndex: ['contact', 'email'],
				width: 300,
				// Plain-text columns ellipsize rather than wrap, so a long value cannot push the
				// row taller than the trimmed padding allows.
				ellipsis: true,
				// The column is narrow enough to truncate, so hovering must reveal the full
				// address. antd's own ellipsis title is not applied when the cell renders an
				// element rather than a bare string, hence the explicit Tooltip.
				render: (email) =>
					email ? (
						<Tooltip title={email}>
							<span className="bodyregular">{email}</span>
						</Tooltip>
					) : (
						'--'
					),
			},
			{
				title: 'Role',
				dataIndex: 'role',
				width: 130,
				render: (roles) => <UserRoleTags roles={roles} />,
			},
			{
				title: 'Title',
				dataIndex: ['contact', 'title'],
				width: 120,
				ellipsis: true,
				// Same reasoning as Email: the column truncates, and antd's own ellipsis title is
				// not applied when the cell renders an element rather than a bare string.
				render: (titleValue) =>
					titleValue ? (
						<Tooltip title={titleValue}>
							<span className="bodyregular">{titleValue}</span>
						</Tooltip>
					) : (
						'--'
					),
			},
			{
				title: 'Department',
				dataIndex: ['contact', 'department'],
				width: 130,
				ellipsis: true,
				render: (department) =>
					department ? (
						<Tooltip title={department}>
							<span className="bodyregular">{department}</span>
						</Tooltip>
					) : (
						'--'
					),
			},
		],
		[userType]
	);

	const hasSelection = selectedEmails.length > 0;

	return (
		<Modal
			visible={true}
			width={1200}
			title={nullSafeGetOrElse('name', userGroup, 'User Group')}
			closable
			onCancel={onCancel}
			// No bottom bar: every action in this modal lives in the header row, so an
			// otherwise-empty footer was just dead vertical space.
			footer={null}
		>
			<div className="mb-4 flex flex-wrap items-center gap-3">
				<Input.Search
					placeholder="Search by name or email"
					style={{ width: 240 }}
					allowClear
					onChange={onSearchChange}
				/>
				<Radio.Group
					className="userGroupStatusFilter"
					value={statusFilter}
					onChange={onStatusFilterChange}
				>
					<Radio.Button value={STATUS_ALL}>{`All - ${statusCounts[STATUS_ALL]}`}</Radio.Button>
					<Radio.Button
						value={STATUS_ACTIVE}
					>{`Active - ${statusCounts[STATUS_ACTIVE]}`}</Radio.Button>
					<Radio.Button
						value={STATUS_INACTIVE}
					>{`Inactive - ${statusCounts[STATUS_INACTIVE]}`}</Radio.Button>
					<Radio.Button
						value={STATUS_INVITED}
					>{`Invited - ${statusCounts[STATUS_INVITED]}`}</Radio.Button>
				</Radio.Group>
				{/* Selection count sits immediately left of the action button, which swaps from
				    Edit to Remove once anything is selected. */}
				<div className="ml-auto flex items-center gap-3">
					{hasSelection ? (
						<span className="footnoteregular whitespace-nowrap text-character-secondary">
							{`Selected: ${selectedEmails.length}`}
						</span>
					) : null}
					{hasSelection ? (
						<Popconfirm
							title={`Remove ${selectedEmails.length} ${
								selectedEmails.length === 1 ? 'user' : 'users'
							} from this group?`}
							onConfirm={onRemoveSelected}
							okText="Remove"
							okButtonProps={{ danger: true }}
							cancelText="Cancel"
						>
							<Button danger type="primary" icon={<DeleteOutlined />} loading={removing}>
								Remove
							</Button>
						</Popconfirm>
					) : (
						<Button
							type="primary"
							icon={<EditOutlined />}
							loading={!!editLoading}
							onClick={onEditClick}
						>
							Edit
						</Button>
					)}
				</div>
			</div>
			{loading ? (
				<div className="flex justify-center py-8">
					<Spin />
				</div>
			) : (
				<Table
					className="userGroupDetailTable"
					columns={columns}
					dataSource={visibleMembers}
					rowKey={emailOf}
					pagination={false}
					scroll={{ y: 420 }}
					rowSelection={{
						selectedRowKeys: selectedEmails,
						onChange: onSelectionChange,
					}}
				/>
			)}
		</Modal>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchUsers: () => dispatch(fetchFacilityUsers(ownProps.userType, {})()),
	removeMembers: (entity) => dispatch(userGroupsRestCrudThunks(ownProps.userType).update(entity)),
});

export default withUserType(connect(null, mapDispatchToProps)(UserGroupDetailModal));
